import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';

import type { CommendaAiConversationAccessView } from '@commenda/api/commenda-ai/types';
import { PanelChat } from '@commenda/ui/commenda-ai/PanelChat';
import { useChatPanel } from 'context/useChatPanel';

import { ChatPanelHeaderActions } from './ChatPanelHeader';
import { ChatPanelPageContextPill } from './ChatPanelPageContextPill';
import { ChatPanelHelper } from './helpers/chat-panel-helper';
import { PageContextHelper } from './helpers/page-context-helper';
import { useChatPanelConversation } from './useChatPanelConversation';
import { useChatPanelSurface } from './useChatPanelSurface';
import { usePageDescriptor } from './usePageDescriptor';

/** The panel's body: header, then either a fresh empty state with its own composer, or the existing
 * conversation surface. The conversation itself is `CommendaAiLoader`, the same component the full
 * page mounts, so the two surfaces can never drift apart. */
export const ChatPanelAside = () => {
  const router = useRouter();
  const { close, activeConversationId, setActiveConversationId } =
    useChatPanel();
  const { startConversation } = useChatPanelConversation();
  const surface = useChatPanelSurface();
  const descriptor = usePageDescriptor();

  // Dismissal is remembered as the path it happened on, so navigating or refreshing re-arms the
  // context: a new page is a new statement about what the assistant can see.
  const [dismissedPath, setDismissedPath] = useState<string | null>(null);
  const isContextDismissed = dismissedPath === router.asPath;

  const getPageContext = useCallback(
    () => (isContextDismissed ? null : PageContextHelper.harvest(descriptor)),
    [descriptor, isContextDismissed]
  );

  const pageContextPill =
    descriptor && !isContextDismissed ? (
      <ChatPanelPageContextPill
        label={descriptor.label}
        onDismiss={() => setDismissedPath(router.asPath)}
      />
    ) : null;

  const renderHeaderTrailing = (access?: CommendaAiConversationAccessView) => (
    <ChatPanelHeaderActions
      access={access}
      activeConversationId={activeConversationId}
      onStartNewConversation={() => setActiveConversationId(null)}
      onSelectConversation={setActiveConversationId}
      onExpand={() => {
        const target = activeConversationId
          ? `/agent?c=${activeConversationId}`
          : '/agent';

        ChatPanelHelper.runWithViewTransition(() => {
          close();
          return router.push(target);
        });
      }}
      onClose={close}
    />
  );

  return (
    <PanelChat
      conversationId={activeConversationId}
      emptyCopy={ChatPanelHelper.getSurfaceCopy(surface)}
      onStartConversation={startConversation}
      isDataRoomChat
      headerTrailing={(access) => renderHeaderTrailing(access)}
      contextPill={
        pageContextPill ? (
          <div className="flex max-w-full pl-xxs">{pageContextPill}</div>
        ) : null
      }
      getPageContext={getPageContext}
    />
  );
};
